import { cur } from '@/lib/currency';
import { useState } from 'react';
import { ChevronDown, ChevronUp, ExternalLink } from 'lucide-react';

/**
 * Inline peek-list of existing matching entries shown inside a duplicate-
 * warning dialog. Collapsed by default; click "View existing" to expand.
 *
 * Props:
 *   - rows:    array of items returned by /check-duplicate `existing` field
 *   - kind:    'sale' | 'expense' | 'supplier_payment' (drives label rendering)
 *   - onItemClick (optional): (id) => void — handler for the row link
 */
export function DuplicatePeekList({ rows = [], kind = 'sale', onItemClick }) {
  const [open, setOpen] = useState(false);
  if (!rows || rows.length === 0) return null;

  const fmtAmt = (n) => `${cur()} ${parseFloat(n || 0).toFixed(2)}`;
  const fmtDate = (iso) => {
    if (!iso) return '';
    try { return new Date(iso).toLocaleDateString(); } catch { return iso.slice(0, 10); }
  };

  const renderRow = (r, i) => {
    let primary = '';
    let secondary = '';
    if (kind === 'sale') {
      primary = `${r.customer_name || 'Walk-in'} • ${fmtAmt(r.amount)}`;
      secondary = [fmtDate(r.date), r.invoice_number && `Inv ${r.invoice_number}`, r.payment_mode].filter(Boolean).join(' · ');
    } else if (kind === 'expense') {
      primary = `${r.category || 'Expense'} • ${fmtAmt(r.amount)}`;
      const who = r.supplier_name || r.employee_name;
      secondary = [fmtDate(r.date), who, r.invoice_number && `Inv ${r.invoice_number}`, r.payment_mode, r.description].filter(Boolean).join(' · ');
    } else {
      primary = `${r.supplier_name || 'Supplier'} • ${fmtAmt(r.amount)}`;
      secondary = [fmtDate(r.date), r.payment_mode, r.notes].filter(Boolean).join(' · ');
    }
    const Wrapper = onItemClick ? 'button' : 'div';
    const wrapperProps = onItemClick
      ? { type: 'button', onClick: () => onItemClick(r.id), className: 'w-full text-left' }
      : {};
    return (
      <Wrapper
        key={r.id || i}
        {...wrapperProps}
        data-testid={`dup-peek-row-${i}`}
        className={`p-2 rounded-md bg-orange-50/60 border border-orange-200/60 hover:bg-orange-100/60 transition-colors ${onItemClick ? 'cursor-pointer w-full text-left' : ''}`}
      >
        <div className="text-xs font-semibold text-orange-900 flex items-center gap-1">
          {primary}
          {onItemClick && <ExternalLink size={10} className="text-orange-600/70" />}
        </div>
        {secondary && <div className="text-[10px] text-orange-700/80 mt-0.5 truncate">{secondary}</div>}
      </Wrapper>
    );
  };

  return (
    <div className="mt-2" data-testid="duplicate-peek-list-wrap">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="text-xs font-medium text-orange-700 hover:text-orange-900 inline-flex items-center gap-1 underline-offset-2 hover:underline"
        data-testid="duplicate-peek-toggle"
      >
        {open ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
        {open ? 'Hide existing entries' : `View existing ${kind === 'sale' ? 'sale(s)' : kind === 'expense' ? 'expense(s)' : 'payment(s)'}`}
      </button>
      {open && (
        <div className="mt-2 space-y-1.5 max-h-48 overflow-y-auto pr-1" data-testid="duplicate-peek-list">
          {rows.map(renderRow)}
        </div>
      )}
    </div>
  );
}
